import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { LightArticle } from "@domain/lms/article/article";
import { Feedback } from "@domain/lms/article/feedback/feedback";
import { fullName } from "@domain/user/utils";
import { formatFeedbackDateToHour } from "@utils/date";
import { Card } from "@ui/components/containers/card";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { gap } from "@ui/styles/utils";
import { FeedbackModal } from "./feedbackModal";

function getFeedbackDate(lastUpdateDate: Date, wording: string): string {
	return I18n.t(wording, { date: lastUpdateDate.toLocaleDateString() }).concat(
		" ",
		I18n.t("article.feedback.created_hour", { hour: formatFeedbackDateToHour(lastUpdateDate) })
	);
}

interface FeedbackViewProps extends React.HTMLAttributes<HTMLDivElement> {
	feedback: Feedback;
	article: LightArticle;
}
export const FeedbackView: React.FC<FeedbackViewProps> = ({ feedback, article, ...htmlProps }) => {
	const { user, text, lastUpdateDate, createdAt } = feedback;
	const currentProfile = useCurrentProfile();

	const isUpdated = lastUpdateDate.getTime() !== createdAt.getTime();

	const askDeleteConfirmation = useConfirmation(I18n.t("article.delete_feedback.title"), I18n.t("delete"));
	const { articleService } = useServices();
	const { createModal } = useModalTools();

	const isAuthor = currentProfile && currentProfile.id === user.id ? true : false;

	return (
		<MainContainer {...htmlProps}>
			<Container>
				<Link to={Path.Profile(user)}>
					<ProfilePicture picture={user.avatar} size={40} />
				</Link>
				<FeedbackContent>
					<Link to={Path.Profile(user)}>
						<Author>{fullName(user)}</Author>
						<Description>{user.description}</Description>
					</Link>
				</FeedbackContent>
				{!!currentProfile && (
					<KebabMenu closeOnSelect>
						<SimpleMenuContent
							onDelete={
								isAuthor
									? () =>
											askDeleteConfirmation(async () => {
												await articleService.deleteFeedback(article.id, feedback.id);
											})
									: undefined
							}
							onEdit={
								isAuthor
									? () =>
											createModal((close) => <FeedbackModal close={close} article={article} feedbackId={feedback.id} />)
									: undefined
							}
							reportInfos={
								isAuthor
									? undefined
									: {
											contentId: article.id,
											contentType: "ARTICLE",
										}
							}
						/>
					</KebabMenu>
				)}
			</Container>
			{text && <Comment>{text}</Comment>}
			<ScoreAndDate>
				<Score></Score>
				<PublishedAndEditedDate>
					<DateText>{getFeedbackDate(createdAt, "article.feedback.created_date")}</DateText>
					{isUpdated && <DateText>{getFeedbackDate(lastUpdateDate, "article.feedback.updated_date")}</DateText>}
				</PublishedAndEditedDate>
			</ScoreAndDate>
		</MainContainer>
	);
};

const MainContainer = styled(Card)`
	padding: 16px;
	display: flex;
	position: relative;
	flex-direction: column;
`;

const Container = styled.div`
	display: flex;
	position: relative;
`;

const FeedbackContent = styled.div`
	padding-left: 20px !important;
	${({ theme }) => theme.textStyles.primary};
	flex: 1;
`;

const Author = styled.b`
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	font-size: 14px;
	line-height: 18px;
	/* identical to box height */

	color: #000000;
`;
const Description = styled.div`
	font-family: Sora;
	font-style: normal;
	font-weight: 300;
	font-size: 12px;
	line-height: 15px;

	color: #626262;
`;
const Comment = styled.div`
	margin-top: 20px;
	font-family: Sora;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 22px;
	/* or 157% */

	color: #4b4b4b;

	@media (min-width: 900px) {
		margin-left: 60px;
	}
`;

const ScoreAndDate = styled.div`
	margin-top: 5px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	${({ theme }) => theme.textStyles.label};
	color: ${({ theme }) => theme.colors.textTertiary};

	@media (max-width: 900px) {
		flex-direction: column;
		align-items: flex-end;
	}
`;

const Score = styled.div`
	display: flex;
	align-items: center;
	${gap(6, "row")}
	svg {
		width: 18px;
	}
	@media (max-width: 900px) {
		align-self: flex-start;
	}
`;

const PublishedAndEditedDate = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1px;
	align-items: end;
	margin-right: 10px;
`;
const DateText = styled.div`
	font-weight: 400;
`;
